import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { emittedRetentionDocApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  PropsEmittedRetentionDocumentI,
  EmittedRetentionDocumentI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

const getEmittedRetentionDocuments = async ({
  pagination,
  sorting,
  globalFilter,
  dateFrom,
  dateTo,
  dateField,
}: PropsEmittedRetentionDocumentI): Promise<
  ServiceResponseI<DataI<EmittedRetentionDocumentI>>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    params.append("dateField", dateField ?? "");
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);

    const { data } = await emittedRetentionDocApi.get<
      ServiceResponseI<DataI<EmittedRetentionDocumentI>>
    >("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const deleteEmittedRetentionDocument = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await emittedRetentionDocApi.delete<
      ServiceResponseI<string>
    >(`/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// Solo PDF/RIDE — a diferencia de las retenciones recibidas, las emitidas NO
// tienen XML (el portal de emitidos del SRI solo entrega el RIDE en PDF).
export const getEmittedRetentionDocumentPdf = async (
  id: string,
): Promise<Blob> => {
  const token = ValidateCurrentToken();
  if (!token) throw new Error("TOKEN INVALIDO");
  const { data } = await emittedRetentionDocApi.get(`/${id}/pdf`, {
    headers: { Authorization: token },
    responseType: "blob",
  });
  return data;
};

// NOTA: acá NO va un syncRetenciones/`/sync` como en recibidas. Las retenciones
// emitidas se sincronizan por la cola de tareas (SriSyncJobPanel, tipo
// "retenciones_emitidas") + la extensión, no por un POST directo.

export const useEmittedRetentionDocuments = ({
  sorting,
  pagination,
  globalFilter,
  dateFrom,
  dateTo,
  dateField,
}: PropsEmittedRetentionDocumentI) => {
  const queryClient = useQueryClient();

  const emittedRetentionDocumentQuery = useQuery(
    [
      "emitted-retention-documents",
      { pagination, sorting, globalFilter, dateFrom, dateTo, dateField },
    ],
    () =>
      getEmittedRetentionDocuments({
        pagination,
        sorting,
        globalFilter,
        dateFrom,
        dateTo,
        dateField,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  const deleteOneMutation = useMutation(
    (id: string) => deleteEmittedRetentionDocument(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["emitted-retention-documents"]);
      },
    },
  );

  const deleteBulkMutation = useMutation(
    (ids: string[]) =>
      Promise.all(ids.map((id) => deleteEmittedRetentionDocument(id))),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["emitted-retention-documents"]);
      },
    },
  );

  return {
    emittedRetentionDocumentQuery,
    deleteOneMutation,
    deleteBulkMutation,
  };
};